import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck, Clock as ClockIcon, AlertTriangle, FileX2, UserX, Mail, Users, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Kpi({ icon: Icon, label, value, color, link }) {
  const content = (
    <Card className={cn('rounded-sm hover:border-primary/60 transition-colors h-full')}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Icon className={cn('w-5 h-5', color)} />
        </div>
        <p className={cn('text-3xl font-barlow', color)}>{value}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{label}</p>
      </CardContent>
    </Card>
  );
  return link ? <Link to={link} className="block">{content}</Link> : content;
}

export default function ComplianceDashboardPage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/compliance/dashboard`); setData(res.data); }
    catch (e) { if (e.response?.status !== 403) toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportEmployees = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees-compliance/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'employees-compliance.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (loading || !data) {
    return <Layout><div className="p-10 text-center text-muted-foreground animate-pulse">Loading…</div></Layout>;
  }
  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="compliance-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-primary" /> HR &amp; COMPLIANCE
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Document compliance overview across all employees and supervisors.
            </p>
          </div>
          <Button onClick={exportEmployees} variant="outline" className="rounded-sm uppercase tracking-wider text-xs" data-testid="export-employees-btn">
            <Download className="w-3.5 h-3.5 mr-2" /> Export Employees (Excel)
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Kpi icon={ClockIcon}     label="Documents pending review" value={data.pending_review} color="text-amber-400"  link={hrMode ? '/employee/hr/compliance-queue' : '/admin/compliance-queue'} />
          <Kpi icon={AlertTriangle} label="Expiring in 30 days"      value={data.expiring_within_30_days} color="text-amber-400" />
          <Kpi icon={FileX2}        label="Expired documents"        value={data.expired} color="text-red-400" />
          <Kpi icon={UserX}         label="Missing required docs"    value={data.employees_missing_required} color="text-red-400" />
          <Kpi icon={ShieldCheck}   label="Fully compliant employees"value={data.fully_compliant_count} color="text-green-400" />
          <Kpi icon={UserX}         label="Non-compliant employees"  value={data.non_compliant_count} color="text-red-400" />
          <Kpi icon={Users}         label="Total active employees"   value={data.total_employees} color="text-foreground" />
        </div>

        <div className="text-xs text-muted-foreground">
          Mandatory document types: {(data.mandatory_doc_types || []).join(', ') || 'none configured'}
        </div>
      </div>
    </Layout>
  );
}
